import { ComponentType, lazy, LazyExoticComponent } from "react";

/**
 * lazy() wrapper that survives a stale/failed chunk fetch.
 *
 * After a deploy, CloudFront serves a fresh index.html while a previously
 * hashed chunk may already be invalidated (deploy.yml invalidates /* on every
 * push), so the dynamic import() rejects with ChunkLoadError and — with only a
 * Suspense fallback and no boundary — blanks the page.
 *
 * Strategy: try the import; on failure retry ONCE (covers a transient hiccup);
 * if it still fails and we have not already reloaded this session, set a
 * sessionStorage guard and do a single window.location.reload() to pull the
 * new index.html + chunk graph. The guard prevents a reload loop and is cleared
 * on any successful load so a future deploy can recover again. If a reload was
 * already attempted, rethrow so the app-level ErrorBoundary shows the BR-PT
 * recovery screen instead of looping.
 */
const RELOAD_GUARD_KEY = "chunkReloadAttempted";

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const component = await factory();
      sessionStorage.removeItem(RELOAD_GUARD_KEY);
      return component;
    } catch (err) {
      // Retry once before resorting to a reload.
      try {
        const component = await factory();
        sessionStorage.removeItem(RELOAD_GUARD_KEY);
        return component;
      } catch (retryErr) {
        if (!sessionStorage.getItem(RELOAD_GUARD_KEY)) {
          sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
          window.location.reload();
          // Hold the render until the reload takes over.
          return new Promise<{ default: T }>(() => {});
        }
        throw retryErr;
      }
    }
  });
}
